/**
 * Chamadas HTTP das residências.
 *
 * Papel: aqui ficam as quatro chamadas de `/api/residencias` que a tela usa —
 * listar, criar, alterar e excluir uma casa.
 *
 * Como usar: a listagem devolve o array inteiro, e não uma página; não há
 * parâmetro de busca nem de tamanho para passar, porque são duas casas. Nenhuma
 * transformação acontece aqui — o que sai é o que o schema validou —, de modo
 * que uma regra de tela colocada neste arquivo deixaria de valer para quem
 * chamasse o service de outro lugar.
 */
import { type NovaResidencia, type Residencia, residenciaSchema } from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

/** A listagem inteira, sem envelope de paginação. */
const listaDeResidenciasSchema = z.array(residenciaSchema);

/**
 * Lê todas as residências, em ordem alfabética de nome.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As casas cadastradas, possivelmente nenhuma.
 */
export function buscarResidencias(sinal?: AbortSignal): Promise<Residencia[]> {
  return clienteApi.get('/residencias', { schema: listaDeResidenciasSchema, sinal });
}

/**
 * Cria uma residência.
 *
 * A API responde 409 quando já existe casa com o mesmo `nome`, e o formulário
 * precisa tratar essa rejeição: é o caso de alguém cadastrar a mesma residência
 * outra vez com grafia diferente, que é o defeito que este cadastro veio
 * consertar.
 *
 * @param dados - A casa preenchida no formulário, já validada por
 *   `novaResidenciaSchema`.
 * @returns A residência como ficou gravada, com identificador e carimbos.
 */
export function criarResidencia(dados: NovaResidencia): Promise<Residencia> {
  return clienteApi.post('/residencias', dados, { schema: residenciaSchema });
}

/**
 * Substitui todos os campos de uma residência.
 *
 * A API responde 409 aqui pelo mesmo motivo que na criação: renomear uma casa
 * para o nome que a outra já tem esbarra na mesma restrição de unicidade, e
 * nesse caso nada é alterado.
 *
 * @param id - Identificador da linha em `residencias`.
 * @param dados - A casa inteira, e não apenas os campos alterados.
 * @returns A residência como ficou gravada.
 */
export function atualizarResidencia(id: string, dados: NovaResidencia): Promise<Residencia> {
  return clienteApi.put(`/residencias/${encodeURIComponent(id)}`, dados, {
    schema: residenciaSchema,
  });
}

/**
 * Apaga uma residência.
 *
 * Não recebe schema porque a API responde 204 sem corpo, e o parse falharia
 * justamente no caminho de sucesso. A API responde 409 quando a casa ainda é
 * apontada por outro cadastro, e nesse caso nada é apagado.
 *
 * @param id - Identificador da linha em `residencias`.
 */
export function excluirResidencia(id: string): Promise<void> {
  return clienteApi.remover(`/residencias/${encodeURIComponent(id)}`);
}
